import { mdiPuzzle } from '@mdi/js';
import Icon from '@mdi/react';
import { Divider, IconButton, Stack, SxProps } from '@mui/material';
import React, { Dispatch, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setNextArea } from '../../../redux/user/sliceUserSettings';
import { IAppReduxStore } from '../../../redux/store';
import { vars } from '../../../Theme';
import { getRedirectUrl } from '../../../util/ProjectUtils';
import AppTooltip from '../../General/AppTooltip/AppTooltip';

const bottomSectionAreaNames = ['configuration', 'filing'];

/**
 * Component that shows the user's available Project Areas in column layout.
 */
const AreaMenu = () => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get relevant values from Redux store. */
    const areas = useSelector((store: IAppReduxStore) => store.projectAreas.data);
    const selectedDocuments = useSelector(
        (_store: IAppReduxStore) => _store.projectSelectedDocuments.storage.documentIds,
    );

    /** Get the replicaId and areaName from the react router. */
    const { replicaId, areaName } = useParams();

    /** Allow component to change the URL. */
    const navigate = useNavigate();

    /**
     * Gets the styling rules for an area button.
     * @param active - if this button is active or not.
     */
    const getButtonSx = (active: boolean): SxProps => ({
        color: active ? vars.mainGreen : vars.font,
        backgroundColor: active ? vars.selectedBgGreen : 'transparent',
        textTransform: 'none',
        width: '48px',
        height: '48px',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: active ? vars.bgGreenHover : vars.fillGray,
            color: active ? vars.selectedHoveredGreen : vars.font,
        },
    });

    /**
     * Handles navigating the user to a new project area, or warns them about losing their document selection.
     * @param areaId - Name of the area that the user clicked on.
     */
    const handleClick = (areaId: string) => {
        if (areaId === areaName) {
            return;
        }

        selectedDocuments.length > 0
            ? dispatch(setNextArea(areaId))
            : navigate(getRedirectUrl(replicaId, areaId));
    };

    const getButton = (x: string, index: number) => {
        return (
            <AppTooltip
                key={`row_${index + 1}`}
                title={areas[x]?.translations[i18n.language] ?? x}
                placement='right'
                type={x === areaName ? 'primary' : 'default'}
            >
                <IconButton sx={getButtonSx(x === areaName)} onClick={() => handleClick(x)}>
                    <Icon path={areas[x]?.icon ?? mdiPuzzle} size={1} />
                </IconButton>
            </AppTooltip>
        );
    };

    /** Create the column that houses the upper area buttons. */
    const topRows = useMemo(() => {
        return Object.keys(areas)
            .filter(x => !bottomSectionAreaNames.includes(areas[x].name))
            .sort((a, b) => areas[a]?.order - areas[b]?.order)
            .map((x, index) => getButton(x, index));
    }, [areas, areaName, selectedDocuments, i18n.language]);

    /** Create the column that houses the lower area buttons. */
    const bottomRows = useMemo(() => {
        return Object.keys(areas)
            .filter(x => bottomSectionAreaNames.includes(areas[x].name))
            .sort((a, b) => areas[a]?.order - areas[b]?.order)
            .map((x, index) => getButton(x, index));
    }, [areas, areaName, selectedDocuments]);

    return (
        <Stack
            sx={{
                flexGrow: 4,
                justifyContent: 'space-between',
            }}
        >
            <Stack
                spacing={'2px'}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                {topRows}
            </Stack>
            <Stack
                spacing={'2px'}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                {bottomRows}
            </Stack>
        </Stack>
    );
};

/**
 * Component that allows the user to change the area of the Project that they are working in.
 */
export default () => {
    /** Get relevant values from Redux store. */
    const areas = useSelector((store: IAppReduxStore) => store.projectAreas.data);

    if (!areas) {
        return <></>;
    }

    return (
        <>
            <Stack sx={{ p: { xs: '8px 4px', md: '8px 12px' }, overflow: 'auto' }}>
                <AreaMenu />
            </Stack>
            <Divider orientation='vertical' flexItem />
        </>
    );
};
